import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { rethrowUnlessAbort } from "../../utils/fetch-error.util"

const NAMED_FIELD = "input[name], select[name], textarea[name]"
const ITEM_REMOVED_EVENT = "documents--items-manager:itemRemoved"
// The sidebar container the server's turbo-stream updates.
const PANEL_ID = "errors"

// Connects to data-controller="documents--mandatory-items-live", on the
// document <form>.
//
// Keeps the sidebar's "Povinné položky" checklist in step with the form. The
// rules stay on the server: on every change the form is posted, unsaved, to
// `<form action>/validation`, which builds the document in memory and answers
// with a turbo-stream that redraws the panel. So a required field that depends
// on another one (the bank account for a bank transfer) follows the form
// without a second copy of the rules here.
//
// The form is replaced as a whole after a failed save, so this controller
// reconnects with the new markup; the panel the failure rendered stays until
// the user changes something.
export default class extends Controller {
  static values = { delay: { type: Number, default: 400 } }

  // Monotonic across reconnects on purpose: resetting it in connect() would let
  // a still-pending request think it is the newest one again.
  #generation = 0
  #abortController = null
  #timer = null
  #observer = null

  connect() {
    this.schedule = this.schedule.bind(this)
    this.element.addEventListener("input", this.schedule)
    this.element.addEventListener("change", this.schedule)
    // Removing a row only hides it and sets its `_destroy` — no input event,
    // no DOM removal — so the items manager's own event is the signal.
    this.element.addEventListener(ITEM_REMOVED_EVENT, this.schedule)

    // Rows are added by turbo-streams, which fire no input event.
    // Started last, so nothing connect() itself does can trigger it.
    this.#observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => this.#changesRows(mutation))) this.schedule()
    })
    this.#observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    this.element.removeEventListener("input", this.schedule)
    this.element.removeEventListener("change", this.schedule)
    this.element.removeEventListener(ITEM_REMOVED_EVENT, this.schedule)
    this.#observer?.disconnect()
    this.#observer = null
    window.clearTimeout(this.#timer)
    this.#timer = null
    this.#abortController?.abort()
    this.#abortController = null
  }

  schedule() {
    // A page whose sidebar has no panel to redraw (the recurring template's
    // settings sidebar) shares the form; asking the server would be wasted.
    if (!document.getElementById(PANEL_ID)) return

    window.clearTimeout(this.#timer)
    this.#timer = window.setTimeout(() => this.refresh(), this.delayValue)
  }

  async refresh() {
    this.#abortController?.abort()
    const abortController = new AbortController()
    this.#abortController = abortController
    const generation = ++this.#generation

    try {
      const response = await fetch(this.#validationUrl(), {
        method: "POST",
        body: this.#formData(),
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": this.#csrfToken() },
        signal: abortController.signal
      })
      // A slower earlier response must never land after a newer one, and a
      // disconnected form must not redraw a panel the page has moved on from.
      if (generation !== this.#generation || !this.element.isConnected) return

      const contentType = response.headers.get("content-type") || ""
      if (!response.ok || !contentType.includes("text/vnd.turbo-stream.html")) return

      Turbo.renderStreamMessage(await response.text())
    } catch (error) {
      rethrowUnlessAbort(error)
    } finally {
      if (this.#abortController === abortController) this.#abortController = null
    }
  }

  #validationUrl() {
    const url = new URL(this.element.action, window.location.origin)
    url.pathname = `${url.pathname.replace(/\/$/, "")}/validation`
    url.search = ""
    return url.toString()
  }

  // `_method` would turn the POST into the edit form's PATCH, and the form's
  // own token is per-form: it is bound to the form's action, not to this one.
  // Files never decide a required field, and re-sending an attached PDF on
  // every keystroke pause would upload it again each time.
  #formData() {
    const data = new FormData(this.element)
    data.delete("_method")
    data.delete("authenticity_token")
    for (const [key, value] of [...data.entries()]) {
      if (value instanceof File) data.delete(key)
    }
    return data
  }

  #csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  // Only a field the form submits counts. Widgets inject unnamed inputs of
  // their own (SlimSelect's search box) as they connect, which must not cost
  // a request on every page load.
  #changesRows(mutation) {
    const nodes = [...mutation.addedNodes, ...mutation.removedNodes]
    return nodes.some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE && (node.matches?.(NAMED_FIELD) || node.querySelector?.(NAMED_FIELD))
    )
  }
}
